import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Lock, AlertTriangle, Clock, X, RotateCcw, Circle } from "lucide-react";
import { cn } from "@/lib/utils";
import { AssignmentSubmissionStatus } from "@/hooks/useAssignments";
import { getAssignmentStatusLabel, getAssignmentStatusColor } from "@/lib/progression";

interface ModuleLockCheckProps {
  reason: string;
  missingAssignments: { id: string; title: string; status: AssignmentSubmissionStatus | null }[];
  courseId: string;
  moduleId?: string;
  onViewAssignments?: () => void;
}

export function ModuleLockCheck({
  reason,
  missingAssignments,
  courseId,
  moduleId,
  onViewAssignments,
}: ModuleLockCheckProps) {
  const getStatusIcon = (status: AssignmentSubmissionStatus | null) => {
    switch (status) {
      case null:
        return <Circle className="h-4 w-4" />;
      case "submitted":
        return <Clock className="h-4 w-4" />;
      case "passed":
        return null; // Shouldn't appear in missing list
      case "failed":
        return <X className="h-4 w-4" />;
      case "retry":
        return <RotateCcw className="h-4 w-4" />;
      default:
        return <Circle className="h-4 w-4" />;
    }
  };

  return (
    <Card className="border-orange-200 dark:border-orange-800 bg-orange-50/50 dark:bg-orange-950/20">
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          <div className="p-2 rounded-full bg-orange-100 dark:bg-orange-900/50">
            <Lock className="h-5 w-5 text-orange-600 dark:text-orange-400" />
          </div>

          <div className="flex-1 space-y-4">
            <div>
              <h3 className="font-semibold text-foreground flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                Cannot Proceed Yet
              </h3>
              <p className="text-sm text-muted-foreground mt-1">{reason}</p>
            </div>

            {missingAssignments.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-foreground">
                  Required Assignments to Complete:
                </p>
                <ul className="space-y-2">
                  {missingAssignments.map((assignment) => (
                    <li
                      key={assignment.id}
                      className="flex items-center gap-3 text-sm p-2 rounded-lg bg-background border"
                    >
                      <span className={cn(getAssignmentStatusColor(assignment.status))}>
                        {getStatusIcon(assignment.status)}
                      </span>
                      <span className="flex-1 font-medium">{assignment.title}</span>
                      <Badge
                        variant="outline"
                        className={cn(
                          "text-xs",
                          assignment.status === "retry" && "border-orange-500 text-orange-600 dark:text-orange-400",
                          assignment.status === "failed" && "border-destructive text-destructive",
                          assignment.status === "submitted" && "border-blue-500 text-blue-600 dark:text-blue-400",
                          !assignment.status && "border-muted-foreground"
                        )}
                      >
                        {getAssignmentStatusLabel(assignment.status)}
                      </Badge>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex flex-wrap gap-2 pt-2">
              {onViewAssignments && (
                <Button variant="default" size="sm" onClick={onViewAssignments}>
                  View Assignments
                </Button>
              )}
              <Button variant="outline" size="sm" asChild>
                <Link to="/curricula">Learning Dashboard</Link>
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
